import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from "input-otp";
import clsx from "clsx";

interface Props {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}

/**
 * Production OTP input — wraps the `input-otp` library (the same one
 * used by shadcn/ui, Vercel, and most large-scale React apps).
 *
 * The library provides a single semantically-correct hidden input with
 * `autocomplete="one-time-code"`, `inputmode="numeric"`, paste-spread,
 * keyboard navigation, and iOS Security Code AutoFill support out of
 * the box. We supply only the visual cell rendering.
 */
export default function OtpInput({
  value,
  onChange,
  length = 6,
  autoFocus,
  disabled,
  hasError,
}: Props) {
  return (
    <OTPInput
      value={value}
      onChange={onChange}
      maxLength={length}
      pattern={REGEXP_ONLY_DIGITS}
      autoFocus={autoFocus}
      disabled={disabled}
      containerClassName="flex items-center justify-center gap-1.5 sm:gap-2 has-[:disabled]:opacity-60"
      render={({ slots }) => (
        <>
          {slots.map((slot, idx) => (
            <Slot key={idx} {...slot} hasError={hasError} />
          ))}
        </>
      )}
    />
  );
}

function Slot({
  char,
  isActive,
  hasFakeCaret,
  hasError,
}: SlotProps & { hasError?: boolean }) {
  return (
    <div
      className={clsx(
        "relative w-10 h-12 sm:w-12 sm:h-14 rounded-lg ring-1 transition",
        "flex items-center justify-center text-xl sm:text-2xl tabular-nums select-none",
        "bg-slate-900/80 text-slate-100",
        hasError
          ? "ring-red-500/60"
          : char
            ? "ring-sky-500/60"
            : "ring-slate-700",
        isActive &&
          (hasError
            ? "ring-2 ring-red-400 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
            : "ring-2 ring-sky-400 shadow-[0_0_0_3px_rgba(56,189,248,0.18)]"),
      )}
    >
      {char}
      {hasFakeCaret && <FakeCaret />}
    </div>
  );
}

function FakeCaret() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="h-6 w-[2px] bg-sky-300 animate-[blink_1s_step-end_infinite]" />
      <style>{`@keyframes blink{50%{opacity:0}}`}</style>
    </div>
  );
}
